/**
 * use-auth-guard.ts — H5 route guard.
 *
 * Every H5 page requires a session. Only the authentication pages themselves are
 * reachable without one: sign-in and registration. A signed-out visitor who asks
 * for any other page is sent to sign-in with the intended destination attached, so
 * completing it returns them where they were going rather than to a default page.
 *
 * This is the product decision for this deployment: it is a members' site whose
 * content is account-scoped, and the root path opens the sign-in page.
 *
 * Why an explicit allow-list of the auth pages, rather than a flag on each route:
 * with a flag, a new page that forgets it would be silently public. Defaulting to
 * "requires a session" means a forgotten page is locked, which is noticed and fixed
 * immediately, instead of exposing account data.
 *
 * How the guard knows whether a session exists: the session lives in an httpOnly
 * cookie the script cannot read, so mirroring it in a client store would be a second
 * source of truth that drifts on expiry, on sign-out in another tab, or on a
 * server-side revocation. The guard therefore asks the server (GET /api/v1/me),
 * which is the same authority every protected endpoint uses. The verdict is cached
 * briefly so navigating between pages does not re-request on every transition.
 */
import { readonly, ref } from "vue";
import type { RouteLocationNormalized, NavigationGuardNext } from "vue-router";

import { readSession } from "@/modules/identity/api/readSession";

/**
 * The only paths reachable without a session.
 *
 * Sign-in and registration are where a signed-out visitor must be able to arrive;
 * everything else redirects here. The root path is included because it redirects to
 * sign-in itself, and letting it pass through avoids a redirect loop.
 */
const AUTH_PATHS = new Set(["/", "/login", "/register"]);

/** How long a positive session check is trusted before asking again. */
const SESSION_CACHE_MS = 15_000;

/**
 * The last server verdict, as reactive state.
 *
 * `null` means "not asked yet" — deliberately distinct from `false`, so a component
 * can render a neutral state on first paint instead of briefly showing a signed-in
 * member the signed-out UI.
 */
const sessionValid = ref<boolean | null>(null);
let checkedAt = 0;

/**
 * Reports whether a path may be reached without a session.
 *
 * Kept as a named export because "can I open this page signed out?" is asked in
 * more than one place, and the answer must have one definition.
 */
export function isPublicPath(path: string): boolean {
  return AUTH_PATHS.has(path);
}

/** Drops the cached verdict; call after sign-in, sign-out or a 401. */
export function invalidateSessionCache(): void {
  sessionValid.value = null;
  checkedAt = 0;
}

/** Asks the server whether the current session is accepted. */
async function hasServerSession(): Promise<boolean> {
  if (sessionValid.value !== null && Date.now() - checkedAt < SESSION_CACHE_MS) {
    return sessionValid.value;
  }
  let valid: boolean;
  try {
    await readSession();
    valid = true;
  } catch {
    // Any failure — 401, 403, or a transport error — is treated as "no session".
    // Assuming a session exists on an unreadable answer would send the visitor to a
    // page that can only render an error.
    valid = false;
  }
  sessionValid.value = valid;
  checkedAt = Date.now();
  return valid;
}

/**
 * Session state for components that adapt to whether a member is signed in.
 *
 * Signing in is now required to see any page, but a component can still render
 * before the session check resolves, and a session can end while a page is open.
 * The answer comes from the same server check the guard uses, so a component and
 * the guard never disagree.
 */
export function useAuth() {
  return {
    /** null = not checked yet, true/false = the server's answer. */
    sessionValid: readonly(sessionValid),
    isSessionValid: (): boolean => sessionValid.value === true,
    refresh: hasServerSession,
    invalidate: invalidateSessionCache,
  };
}

/**
 * Router guard: everything except the auth pages needs a session.
 *
 * The redirect carries the intended path, so signing in returns the member to what
 * they asked for.
 */
export function createAuthGuard() {
  return async (
    to: RouteLocationNormalized,
    _from: RouteLocationNormalized,
    next: NavigationGuardNext,
  ) => {
    if (isPublicPath(to.path)) {
      next();
      return;
    }
    if (await hasServerSession()) {
      next();
      return;
    }
    next({ path: "/login", query: { redirect: to.fullPath } });
  };
}
